import React, { ReactNode, useState, useEffect } from 'react';
import BladeHeader from './BladeHeader';
import MeterInfoSection from './MeterInfoSection';
import bladeStyles from './Blade.module.css';
import BladeFooter from './footer/BladeFooter';
import { toTestAttrValue } from '@utils/testAttr';

const MeterDetailsBlade = ({
  title,
  onClose,
  meterName,
  meterNumber,
  monthYear,
  monthLabel,
  onMonthChange,
  children,
  handleApply,
  applyLabel = 'Apply Changes',
  saving = false,
  saveFeedback,
  isDirty = false,
  open = false,
  accessoryView,
  readOnly = false,
  inlineError,
  infoSection,
}: {
  title: string;
  onClose: () => void;
  meterName?: string | null;
  meterNumber?: string | null;
  monthYear?: string;
  monthLabel: string;
  onMonthChange?: (monthYear: string) => void;
  children: ReactNode;
  handleApply: () => void;
  applyLabel?: string;
  saving?: boolean;
  saveFeedback?: { status: 'success' | 'error'; message: string } | null;
  isDirty?: boolean;
  open?: boolean;
  accessoryView?: ReactNode;
  readOnly?: boolean;
  /** Error message shown in red near the Cancel/Apply footer row */
  inlineError?: string | null;
  /** Override the default MeterInfoSection with custom content */
  infoSection?: ReactNode;
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
    }
  }, [open]);

  return (
    <>
      <div
        className={`${bladeStyles.overlay} ${visible ? bladeStyles.open : ''}`}
        role="presentation"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        data-testid="blade-overlay"
      />
      {/* Blades stay mounted while closed — tests should match data-open="true" */}
      <div
        className={`${bladeStyles.panel} ${visible ? bladeStyles.open : ''} blade-panel`}
        data-testid="blade-panel"
        data-blade={toTestAttrValue(title)}
        data-open={String(open)}
      >
        <BladeHeader title={title} onClose={onClose} />

        <div className={bladeStyles.body} style={{ paddingBottom: 0 }}>
          {infoSection ?? <MeterInfoSection meterName={meterName} meterNumber={meterNumber} monthLabel={monthLabel} monthYear={monthYear} onMonthChange={onMonthChange} />}
          {children}
        </div>

        {accessoryView}

        {/* Inline error banner — sits directly above the footer, aligned to Cancel */}
        {inlineError && (
          <div
            style={{
              padding: '8px 16px',
              background: '#fff2f0',
              borderTop: '1px solid #ffccc7',
              color: '#cf1322',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            data-testid="blade-inline-error"
          >
            {/* Simple error icon */}
            <span style={{ fontSize: 15, lineHeight: 1 }}>⚠</span>
            <span>{inlineError}</span>
          </div>
        )}

        <BladeFooter
          onClose={onClose}
          onApply={handleApply}
          applyLabel={applyLabel}
          saving={saving}
          disabled={readOnly || !isDirty}
          saveFeedback={readOnly ? null : saveFeedback}
        />
      </div>
    </>
  );
};

export default MeterDetailsBlade;